import { useState } from 'react'
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { RegionalSummary } from '@/lib/demographics'
import {
  formatPopulation,
  formatAgeBracket,
  formatEducationLevel,
  formatIncomeLevel,
  formatSettlementType,
} from '@/lib/demographics'

interface RegionalBreakdownProps {
  regions: RegionalSummary[]
  className?: string
}

export function RegionalBreakdown({ regions, className }: RegionalBreakdownProps) {
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())

  const toggleRegion = (regionId: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev)
      if (next.has(regionId)) {
        next.delete(regionId)
      } else {
        next.add(regionId)
      }
      return next
    })
  }

  if (regions.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Regional Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No regions available.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Regional Breakdown</CardTitle>
        <CardDescription>Demographics by region and district</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {regions.map((summary) => {
          const isExpanded = expandedRegions.has(summary.region.id)

          return (
            <div key={summary.region.id} className="border rounded-lg overflow-hidden">
              {/* Region Header */}
              <button
                onClick={() => toggleRegion(summary.region.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <div className="font-medium">{summary.region.name}</div>
                    <div className="text-muted-foreground text-xs flex items-center gap-2">
                      <span>{formatPopulation(summary.totalPopulation)} population</span>
                      <span>|</span>
                      <span>{summary.districtCount} districts</span>
                      <span>|</span>
                      <span>{summary.region.seat_count} seats</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {formatSettlementType(summary.dominantSettlement)}
                  </Badge>
                </div>
              </button>

              {/* Expanded Region Details */}
              {isExpanded && (
                <div className="border-t bg-muted/30">
                  {/* Region Demographics Summary */}
                  <div className="p-3 border-b bg-muted/20">
                    <div className="text-xs font-medium text-muted-foreground mb-2">
                      Dominant Demographics
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {formatAgeBracket(summary.dominantAge).split(' ')[0]}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {formatEducationLevel(summary.dominantEducation)}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {formatIncomeLevel(summary.dominantIncome)}
                      </Badge>
                    </div>
                  </div>

                  {/* District List */}
                  <div className="divide-y">
                    {summary.districts.map((districtSummary) => (
                      <div
                        key={districtSummary.district.id}
                        className="p-3 flex items-center justify-between hover:bg-muted/40"
                      >
                        <div className="flex items-center gap-3">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <div className="text-sm font-medium">
                              {districtSummary.district.name}
                            </div>
                            <div className="text-muted-foreground text-xs flex items-center gap-2">
                              <span>{formatPopulation(districtSummary.totalPopulation)}</span>
                              <span>|</span>
                              <span>{districtSummary.cohortCount} cohorts</span>
                              <span>|</span>
                              <span>{districtSummary.district.seat_count} seats</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {formatSettlementType(districtSummary.district.settlement_type)}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

/**
 * Compact version for smaller displays
 */
interface RegionalBreakdownCompactProps {
  regions: RegionalSummary[]
  className?: string
}

export function RegionalBreakdownCompact({ regions, className }: RegionalBreakdownCompactProps) {
  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Regions</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {regions.map((summary) => (
            <div
              key={summary.region.id}
              className="flex items-center justify-between p-2 rounded-lg border"
            >
              <div>
                <div className="font-medium text-sm">{summary.region.name}</div>
                <div className="text-muted-foreground text-xs">
                  {formatPopulation(summary.totalPopulation)} | {summary.region.seat_count} seats
                </div>
              </div>
              <Badge variant="outline" className="text-xs">
                {formatSettlementType(summary.dominantSettlement)}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
